/**
 * Health over time (2026-09-29). Replaces two charts that plotted hardcoded
 * "Jul–Dec" numbers (a "Strategic alignment" trend on the portfolio
 * dashboard and "Performance trends" on the program dashboard). Data comes
 * from the daily dashboard health snapshots (/dashboard/executive/health-history).
 */
import { useEffect, useState } from 'react'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { dashboardService, type HealthHistoryPoint } from '../../services/dashboard'

type Series = 'portfolio_health' | 'schedule_stability' | 'delivery_confidence' | 'resource_stress'

const SERIES: Record<Series, { name: string; color: string }> = {
  portfolio_health: { name: 'Health score', color: 'var(--chart-1)' },
  schedule_stability: { name: 'On schedule %', color: 'var(--chart-2)' },
  delivery_confidence: { name: 'Delivered on time %', color: 'var(--chart-3)' },
  resource_stress: { name: 'Projects with blocked work %', color: 'var(--chart-4)' },
}

function shortDate(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${d}/${m}`
}

export function HealthTrendChart({ title, series, days = 90, className = '' }: {
  title: string
  series: Series[]
  days?: number
  className?: string
}) {
  const [points, setPoints] = useState<HealthHistoryPoint[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    dashboardService.getHealthHistory(days)
      .then((h) => { if (live) setPoints(h.points) })
      .catch(() => { if (live) { setFailed(true); setPoints([]) } })
    return () => { live = false }
  }, [days])

  const latest = points && points.length ? points[points.length - 1] : null

  return (
    <div className={`rounded-lg border border-border bg-card p-6 shadow-sm ${className}`}>
      <h3 className="mb-1 text-lg font-semibold text-foreground">{title}</h3>
      <p className="mb-4 text-xs text-muted-foreground">Last {days} days · one point per day the dashboard was opened</p>
      {points === null ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
      ) : failed ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Couldn't load the trend.</p>
      ) : points.length < 2 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          The trend starts today and fills in day by day{latest ? ` — health score today: ${Math.round(latest.portfolio_health)}` : ''}.
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={points}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={shortDate} />
              <YAxis domain={[0, 100]} />
              <Tooltip labelFormatter={(v) => shortDate(String(v))} />
              {series.length > 1 && <Legend />}
              {series.map((s) => (
                <Line key={s} type="monotone" dataKey={s} stroke={SERIES[s].color} strokeWidth={2} name={SERIES[s].name} dot={points.length < 20} />
              ))}
            </LineChart>
          </ResponsiveContainer>
          {latest && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{SERIES[series[0]].name} today</span>
              <span className="text-2xl font-bold text-foreground">{Math.round(latest[series[0]])}</span>
            </div>
          )}
        </>
      )}
    </div>
  )
}
